import * as React from 'react';

import { CalendarDate } from '@internationalized/date';
import { enumTrailerStatus } from '@rematter/frontend-lib-api-client';
import { useTranslations } from '@rematter/modules/transportation/i18n/useTranslations';
import { DateField } from '@rematter/ui-web';
import {
  Radio,
  RadioGroupField,
  TextField,
  NumberField,
} from '@rematter/ui-web';
import { useAtomValue, useSetAtom } from 'jotai';

import { TransportationTypeSelect } from '~/modules/transportation/components/@shared/TransportationTypeSelect';
import { VehicleVinTextField } from '~/modules/transportation/components/@shared/VehicleVinTextField';
import {
  $trailerForm,
  $updateTrailerForm,
} from '~/modules/transportation/state/trailerForm.localState';
import { $localization } from '~/shared/state/preferences.state';

type TrailerFormProps = {
  isEditing?: boolean;
};

export const TrailerForm = (_props: TrailerFormProps) => {
  const t = useTranslations();
  const localization = useAtomValue($localization);

  const form = useAtomValue($trailerForm);
  const updateForm = useSetAtom($updateTrailerForm);

  React.useEffect(() => {
    if (form.country === '') {
      updateForm({
        country: localization.defaultCountry,
      });
    }
  }, [form, updateForm, localization.defaultCountry]);

  const handleFieldChange =
    <K extends keyof typeof form>(field: K) =>
    (value: (typeof form)[K]) => {
      updateForm({
        [field]: value,
      });
    };

  const handleCountryChange = (value: string | undefined) => {
    if (value) {
      handleFieldChange('country')(value);
      handleFieldChange('state')('');
    }
  };

  return (
    <div className="@TrailerForm flex flex-col gap-4">
      <RadioGroupField
        label={t.trailersView.form.status}
        defaultValue={form.status}
        onChange={(value: string) =>
          handleFieldChange('status')(value as typeof form.status)
        }
        isRequired
      >
        {Object.values(enumTrailerStatus).map((status) => (
          <Radio key={status} value={status}>
            {t.trailersView.status[status]}
          </Radio>
        ))}
      </RadioGroupField>

      <TransportationTypeSelect
        label={t.trailersView.form.transportationType}
        value={form.transportationTypeId}
        onChange={(value) =>
          handleFieldChange('transportationTypeId')(value ? String(value) : '')
        }
        isRequired
      />

      <div className="grid grid-cols-3 grid-rows-1 gap-4">
        <VehicleVinTextField
          label={t.trailersView.form.vin}
          value={form.vin}
          onChange={handleFieldChange('vin')}
          onSuccess={(vehicleInfo) => {
            // For trailers, we'll just store the year in the notes as additional information
            updateForm({
              notes: form.notes
                ? `${form.notes}\nVehicle Year: ${vehicleInfo.ModelYear} | Make: ${vehicleInfo.Make} | Model: ${vehicleInfo.Model}`
                : `Vehicle Year: ${vehicleInfo.ModelYear} | Make: ${vehicleInfo.Make} | Model: ${vehicleInfo.Model}`,
            });
          }}
        />

        <div className="col-span-2">
          <TextField
            label={t.trailersView.form.licensePlate}
            value={form.licensePlate}
            onChange={handleFieldChange('licensePlate')}
            isRequired
          />
        </div>
      </div>

      <DateField
        label={t.trailersView.form.licensePlateExpiration}
        value={form.licensePlateExpiration}
        onChange={(value: CalendarDate | null) =>
          handleFieldChange('licensePlateExpiration')(value ?? undefined)
        }
      />

      <NumberField
        label={t.trailersView.form.capacity}
        value={form.capacity}
        onChange={(value: number | null) =>
          handleFieldChange('capacity')(value ?? 0)
        }
        isRequired
      />

      <TextField
        label={t.trailersView.form.country}
        value={form.country}
        onChange={(value: string) => handleCountryChange(value)}
        isRequired
      />

      <TextField
        label={t.trailersView.form.state}
        value={form.state}
        onChange={handleFieldChange('state')}
        isRequired
      />

      <TextField
        label={t.trailersView.form.notes}
        value={form.notes}
        onChange={handleFieldChange('notes')}
        type="text"
      />
    </div>
  );
};
